import React, { useEffect, useRef, useState } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import Industries from './Industries.jsx';
import banner from '../assets/banners/img2.avif';
import { FiSettings, FiLayers, FiSearch, FiUsers, FiArrowRight, FiCheckCircle, FiStar, FiMessageSquare, FiAward, FiClock, FiShield, FiTrendingUp } from 'react-icons/fi';
import { FaQuoteLeft } from 'react-icons/fa';
import backgroundImg from '../assets/banners/im1.avif';
import '../css/whyAnuFlowControl.css';

const testimonials = [
  {
    quote: 'ANU Flow Control has been our trusted partner for over 10 years. Their precision-engineered valves have consistently exceeded our quality expectations and have proven reliable in our most critical operations.',
    author: 'Ravi Kumar',
    position: 'Plant Manager',
    company: 'Leading Refinery, Maharashtra',
    rating: 5
  },
  {
    quote: 'The timely delivery and exceptional quality of ANU Flow Control valves have been instrumental in keeping our production lines running smoothly. Their commitment to excellence is unmatched.',
    author: 'Priya Sharma',
    position: 'Procurement Head',
    company: 'Steel Manufacturing Plant, Gujarat',
    rating: 5
  },
  {
    quote: 'Their technical support team is outstanding. They provided customized solutions that perfectly met our requirements. ANU Flow Control truly understands industrial valve needs.',
    author: 'Amit Patel',
    position: 'Operations Director',
    company: 'Chemical Processing Unit, Karnataka',
    rating: 5
  },
  {
    quote: 'We have been using ANU Flow Control valves for our marine applications and they have performed exceptionally well. The quality and durability are second to none.',
    author: 'Captain Rajesh Singh',
    position: 'Marine Engineer',
    company: 'Shipping Corporation, Mumbai',
    rating: 5
  },
  {
    quote: 'The ISO and ASME certified valves from ANU Flow Control give us complete confidence in our operations. Their quality control processes are truly world-class.',
    author: 'Dr. Meera Nair',
    position: 'Quality Assurance Head',
    company: 'Pharmaceutical Plant, Hyderabad',
    rating: 5
  },
  {
    quote: 'ANU Flow Control provided us with customized butterfly valves that perfectly fit our food processing requirements. Their attention to detail and hygiene standards are remarkable.',
    author: 'Suresh Reddy',
    position: 'Operations Manager',
    company: 'Food Processing Unit, Bangalore',
    rating: 5
  },
  {
    quote: 'After switching to ANU Flow Control valves, we noticed significant improvement in our system efficiency. Their products are reliable and cost-effective.',
    author: 'Vikram Joshi',
    position: 'Engineering Head',
    company: 'Power Generation Plant, Delhi',
    rating: 5
  },
  {
    quote: 'The after-sales support from ANU Flow Control is excellent. They respond quickly and their technical team is always ready to help with any queries.',
    author: 'Anjali Desai',
    position: 'Maintenance Manager',
    company: 'Textile Industry, Surat',
    rating: 5
  }
];

const successStories = [
  {
    title: '1000+ Industrial Installations',
    description: 'Successfully supplied and installed valves across diverse industries including refineries, power plants, and chemical processing units.',
    icon: <FiCheckCircle />
  },
  {
    title: 'Global Reach',
    description: 'Exporting high-quality valves to international markets, meeting global standards and building trust worldwide.',
    icon: <FiCheckCircle />
  },
  {
    title: '98% Customer Satisfaction',
    description: 'Consistent delivery of superior quality products and services has earned us the trust of industry leaders.',
    icon: <FiCheckCircle />
  }
];

const reliabilityFeatures = [
  {
    icon: <FiAward className="reliability-icon" />,
    title: 'Consistent Quality',
    description: 'ISO 9001:2015 certified processes ensure every valve meets the highest standards. Rigorous quality control at every stage of manufacturing guarantees reliability and precision.'
  },
  {
    icon: <FiClock className="reliability-icon" />,
    title: 'On-Time Delivery',
    description: 'Commitment to timely delivery with streamlined logistics. Our efficient production and supply chain management ensures you receive your orders exactly when you need them.'
  },
  {
    icon: <FiShield className="reliability-icon" />,
    title: 'Durability and Longevity',
    description: 'Built to last with premium materials and precision engineering. Our valves are designed to withstand extreme conditions and provide years of reliable operation.'
  },
  {
    icon: <FiTrendingUp className="reliability-icon" />,
    title: 'Performance Metrics',
    description: 'Continuously monitored and optimized performance metrics. Track record of 98% customer satisfaction, 1000+ successful installations, and global reach in 20+ countries.'
  }
];

function ReliabilityPerformanceSection() {
  return (
    <section className="reliability-section">
      <div className="reliability-container">
        <div className="reliability-header">
          <h2 className="reliability-title">Reliability & Performance</h2>
          <p className="reliability-subtitle">
            Built on a foundation of consistent quality, timely delivery, and proven performance across diverse industrial applications
          </p>
        </div>

        <div className="reliability-flow">
          <div className="reliability-connector"></div>
          {reliabilityFeatures.map((feature, index) => (
            <div 
              key={index} 
              className="reliability-feature"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="reliability-feature-line">
                <div className="reliability-feature-dot"></div>
              </div>
              <div className="reliability-feature-content">
                <div className="reliability-feature-icon-bg">
                  {feature.icon}
                </div>
                <div className="reliability-feature-text">
                  <h3 className="reliability-feature-title">{feature.title}</h3>
                  <p className="reliability-feature-desc">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustryLeadersSection() {
  return (
    <section className="industry-leaders-section">
      <div className="leaders-container">
        <div className="leaders-header">
          <h2 className="leaders-title">Why Industry Leaders Choose Us</h2>
          <div className="leaders-divider"></div>
          <p className="leaders-subtitle">
            Trusted by leading industries worldwide for precision, reliability, and excellence
          </p>
        </div>

        {/* Testimonials */}
        <div className="testimonials-wrapper">
          <div className="testimonials-marquee">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <div 
                key={index} 
                className="testimonial-card marquee-item"
              >
                <div className="testimonial-icon">
                  <FaQuoteLeft />
                </div>
                <div className="testimonial-rating">
                  {Array(testimonial.rating).fill(0).map((_, i) => (
                    <FiStar key={i} className="star-icon" />
                  ))}
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <h4 className="author-name">{testimonial.author}</h4>
                  <p className="author-position">{testimonial.position}</p>
                  <p className="author-company">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Success Stories */}
        <div className="success-stories">
          <h3 className="success-title">Our Success Metrics</h3>
          <div className="success-divider"></div>
          <div className="success-grid">
            {successStories.map((story, index) => (
              <div 
                key={index} 
                className="success-card"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="success-icon">{story.icon}</div>
                <h4 className="success-card-title">{story.title}</h4>
                <p className="success-card-desc">{story.description}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}


const advantages = [
  {
    icon: <FiSettings className="feature-icon" />,
    title: 'Precision Manufacturing',
    description: 'Hubli plant with full production capabilities. Complete range of industrial valves — Gate, Globe, Check, Ball, and Butterfly Valves. Over 20 years of manufacturing expertise with expansion facility near Chikkaballapur.'
  },
  {
    icon: <FiLayers className="feature-icon" />,
    title: 'Quality Excellence',
    description: 'ISO 9001:2015 certified. Adheres to ASME B16.10, ASME B16.34, ASME B16.5, API 600, API 623, API 6D standards. Integrated manufacturing and testing facilities ensuring precision and reliability at every stage.'
  },
  {
    icon: <FiSearch className="feature-icon" />,
    title: 'Innovation & R&D',
    description: 'Advanced machinery with in-house R&D capabilities. Continuous improvement and innovation in product design and development. Modern manufacturing practices for cutting-edge valve solutions.'
  },
  {
    icon: <FiUsers className="feature-icon" />,
    title: 'Customer Commitment',
    description: 'Dedicated sales and support team maintaining efficient communication with customers. Superior quality assurance, timely delivery, competitive pricing. Customized solutions and reliable after-sales service.'
  }
];

function CompetitiveAdvantagesSection() {
  return (
    <section className="competitive-advantages">
      <div 
        className="advantages-bg-image"
        style={{ '--bg-img': `url(${backgroundImg})` }}
      ></div>
      <div className="advantages-container">
        <div className="advantages-header">
          <h2 className="advantages-title">Our Competitive Advantages</h2>
          <div className="advantages-divider"></div>
        </div>
        
        <div className="advantages-grid">
          {advantages.map((advantage, index) => (
            <div 
              key={advantage.title} 
              className="feature-item"
            >
              <div className="feature__icon">
                {advantage.icon}
              </div>
              <h4 className="feature__title">{advantage.title}</h4>
              <p className="feature__desc">{advantage.description}</p>
              <a href="#" className="btn__link">
                <FiArrowRight className="icon-arrow-right" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const WhyAnuFlowControl = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      <div className="why-anu-banner">
        <img
          src={banner}
          alt="Why AnuFlowControl Banner"
          className="why-anu-banner__img"
        />
        <div className="why-anu-banner__overlay"></div>
        <div className="why-anu-banner__content">
          <h2 className="why-anu-banner__title">Why Anu Flow Controls</h2>
          <div className="why-anu-banner__subtitle">
            Discover what sets us apart in the industrial valve manufacturing industry
          </div>
        </div>
      </div>
      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scroll-section"
      >
        <IndustryLeadersSection />
      </div>
      <div 
        ref={(el) => (sectionsRef.current[1] = el)}
        className="scroll-section"
      >
        <CompetitiveAdvantagesSection />
      </div>
      <div 
        ref={(el) => (sectionsRef.current[2] = el)}
        className="scroll-section"
      >
        <ReliabilityPerformanceSection />
      </div>
      <div 
        ref={(el) => (sectionsRef.current[3] = el)}
        className="scroll-section"
      >
        <Industries />
      </div>
      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default WhyAnuFlowControl;

